/**
 * /reports — generate + view student / class reports.
 *
 * Mode toggle picks whether to operate on a single student or whole
 * class. The actual content is produced by ai.generate_report() on the
 * backend, which gracefully degrades to a mock when no real agent is
 * configured.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { downloadReportPdf } from "./downloadReportPdf";
import { domToMhtml } from "../../lib/mhtml";

import {
  useClassWithRoster,
  useClasses,
  useGenerateClassReport,
  useGenerateStudentReport,
  useReports,
  useUpdateReport,
  type Report,
} from "../../api/school";
import { CustomPromptInput } from "../../components/CustomPromptInput";
import { useMyEmployees } from "../workbench/EmployeeSwitcher";
import { DistributionChart } from "../../components/DistributionChart";
import { RadarChart } from "../../components/RadarChart";
import { SkillBreakdownRows, SkillBreakdownView } from "./skillBreakdown";

type Mode = "student" | "class";

export function ReportsPage() {
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState<Mode>("student");
  const classes = useClasses();
  const [classId, setClassId] = useState("");
  const effectiveClassId = classId || classes.data?.[0]?.id || "";
  const classDetail = useClassWithRoster(effectiveClassId);
  const [studentId, setStudentId] = useState("");
  const effectiveStudentId =
    studentId || classDetail.data?.students?.[0]?.id || "";

  const targetId = mode === "student" ? effectiveStudentId : effectiveClassId;
  const reports = useReports(targetId);

  const genStudent = useGenerateStudentReport();
  const genClass = useGenerateClassReport();
  const [latest, setLatest] = useState<Report | null>(null);
  const [customPrompt, setCustomPrompt] = useState("");
  // 学科维度(Jarod 2026-08-28:"AI 报告这里目前好像只有英语的,是否
  // 可以加其他学科以及综合性报告")。"" = 综合,覆盖该学生所有有数据
  // 的学科;具体 slug = 单科。候选跟老师自己的学科走,和全站一致。
  const { employees: reportEmployees } = useMyEmployees();
  const [subject, setSubject] = useState<string>("");

  const locale = i18n.language.toLowerCase().startsWith("zh") ? "zh-CN" : "en-US";

  async function generate() {
    const prompt = customPrompt.trim() || null;
    const reportLocale = i18n.language || "en";
    if (mode === "student") {
      const r = await genStudent.mutateAsync({
        student_id: effectiveStudentId,
        custom_prompt: prompt,
        locale: reportLocale,
        subject: subject || null,
      });
      setLatest(r);
    } else {
      const r = await genClass.mutateAsync({
        class_id: effectiveClassId,
        custom_prompt: prompt,
        locale: reportLocale,
        subject: subject || null,
      });
      setLatest(r);
    }
  }

  const busy = genStudent.isPending || genClass.isPending;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <header className="mb-6 flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold text-slate-800">{t("reports.title")}</h1>
      </header>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="inline-flex rounded border border-slate-200 overflow-hidden">
          {(["student", "class"] as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`px-3 py-1.5 text-sm ${
                mode === m
                  ? "bg-sky-500 text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              {t(`reports.modes.${m}`)}
            </button>
          ))}
        </div>

        <select
          value={effectiveClassId}
          onChange={(e) => {
            setClassId(e.target.value);
            setStudentId("");
          }}
          className="px-3 py-1.5 rounded border border-slate-200 text-sm"
        >
          {classes.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        {mode === "student" && (
          <select
            value={effectiveStudentId}
            onChange={(e) => setStudentId(e.target.value)}
            className="px-3 py-1.5 rounded border border-slate-200 text-sm min-w-[180px]"
          >
            {classDetail.data?.students.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name_zh} {s.name_en ? `(${s.name_en})` : ""}
              </option>
            ))}
          </select>
        )}

        <select
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="px-3 py-1.5 rounded border border-slate-200 text-sm"
          title={String(t("reports.subjectHint"))}
        >
          <option value="">{t("reports.subjectAll")}</option>
          {reportEmployees.map((emp) => (
            <option key={emp.subject_id} value={emp.slug}>
              {emp.name}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={generate}
          disabled={busy}
          className="px-4 py-1.5 rounded bg-sky-500 text-white text-sm font-medium hover:bg-sky-600 disabled:opacity-50"
        >
          {busy ? t("reports.generating") : t("reports.generate")}
        </button>
      </div>

      <div className="mb-4 max-w-2xl">
        <CustomPromptInput
          value={customPrompt}
          onChange={setCustomPrompt}
          placeholder={t("reports.customPromptPlaceholder")}
          surface={mode === "student" ? "report_student" : "report_class"}
          context={
            mode === "student"
              ? {
                  student_name: classDetail.data?.students.find(
                    (s) => s.id === effectiveStudentId,
                  )?.name_zh,
                  class_name: classes.data?.find((c) => c.id === effectiveClassId)?.name,
                }
              : {
                  class_name: classes.data?.find((c) => c.id === effectiveClassId)?.name,
                  student_count: classDetail.data?.student_count,
                }
          }
        />
      </div>

      {latest && (
        <ReportCard
          report={latest}
          locale={locale}
          onSaved={(updated) => setLatest(updated)}
        />
      )}

      {reports.data && reports.data.length > 0 && (
        <section className="mt-6">
          <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-2">
            {t("reports.historyTitle")}
          </h2>
          <ul className="space-y-2">
            {reports.data.map((r) => (
              <li
                key={r.id}
                className="rounded border border-slate-200 bg-white p-3 cursor-pointer hover:border-sky-300"
                onClick={() => setLatest(r)}
              >
                <div className="flex justify-between text-sm">
                  <span className="text-slate-800">
                    {r.target_type === "student"
                      ? t("reports.row.studentReport")
                      : t("reports.row.classReport")}{" "}
                    · {r.period}
                  </span>
                  <span className="text-xs text-slate-500">
                    {new Date(r.created_at).toLocaleString(locale)}
                  </span>
                </div>
                {r.summary && (
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{r.summary}</p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function ReportCard({
  report,
  locale,
  onSaved,
}: {
  report: Report;
  locale: string;
  /** Notify the parent that the report content was edited and persisted,
   *  so it can swap its `latest` pointer to the fresh server-returned
   *  copy. Without this, exiting edit mode flips back to the pre-edit
   *  render because `report` is a prop the parent owns. */
  onSaved?: (updated: Report) => void;
}) {
  const { t, i18n } = useTranslation();
  const c = report.content;
  const isStudent = report.target_type === "student";
  const radarLang: "zh" | "en" = i18n.language.toLowerCase().startsWith("zh") ? "zh" : "en";

  const update = useUpdateReport();
  const [editing, setEditing] = useState(false);
  // Local drafts for editable fields. Seeded from the current report
  // each time edit mode opens so the form starts from the latest copy
  // (handles "edit → save → edit again" cleanly).
  //
  // Summary is stored on the report as a single string but the read
  // view splits it into clause-by-clause disc bullets (SummaryBullets).
  // Edit mode mirrors that — bullets of the same clauses, one textarea
  // each — so the layout doesn't jump between modes. On save we join
  // back to a single string the agent format expects.
  const [draftSummaryItems, setDraftSummaryItems] = useState<string[]>([]);
  // Cache the "(演示模式…)" / "(demo mode…)" suffix the AI appended (if
  // any) so it round-trips through edit unchanged. The teacher's edits
  // shouldn't accidentally drop that pill, and they shouldn't see it as
  // an editable bullet either.
  const demoTagRef = useRef<string | null>(null);
  const [draftStrengths, setDraftStrengths] = useState<string[]>([]);
  const [draftGrowth, setDraftGrowth] = useState<string[]>([]);
  const [draftRecs, setDraftRecs] = useState<string[]>([]);

  function startEdit() {
    const rawSummary = (c.summary as string) ?? "";
    const { demoTag, body } = splitDemoSuffix(rawSummary);
    demoTagRef.current = demoTag;
    setDraftSummaryItems(splitIntoBullets(body));
    setDraftStrengths(((c.strengths as string[]) ?? []).slice());
    setDraftGrowth(((c.areas_for_growth as string[]) ?? []).slice());
    setDraftRecs(((c.recommendations as string[]) ?? []).slice());
    setEditing(true);
  }

  async function saveEdit() {
    // Rejoin the bullet drafts into the single-string shape the
    // backend + AI format expect. A space between clauses works for
    // both Chinese (full-width terminators already on each item) and
    // English (period + space convention). Empty items are dropped so
    // a teacher can clear a bullet without leaving a "  " gap.
    const summaryBody = draftSummaryItems
      .map((s) => s.trim())
      .filter(Boolean)
      .join(" ");
    const nextSummary = demoTagRef.current
      ? `${summaryBody} (${demoTagRef.current})`.trim()
      : summaryBody;
    const next = {
      ...c,
      summary: nextSummary,
      strengths: draftStrengths.map((s) => s.trim()).filter(Boolean),
      areas_for_growth: draftGrowth.map((s) => s.trim()).filter(Boolean),
      recommendations: draftRecs.map((s) => s.trim()).filter(Boolean),
    };
    const saved = await update.mutateAsync({
      id: report.id,
      content: next,
      summary: next.summary || null,
    });
    // Hand the fresh server copy back to the parent so its `latest`
    // pointer flips to the edited version. Otherwise the parent keeps
    // rendering us with the pre-edit `report` prop and the bullets snap
    // back to what the AI originally produced.
    onSaved?.(saved);
    setEditing(false);
  }

  function cancelEdit() {
    setEditing(false);
  }

  const articleRef = useRef<HTMLElement | null>(null);
  const [downloading, setDownloading] = useState(false);

  async function downloadMhtml() {
    // 单文件网页归档(Jarod 2026-08-25:汇总报告要 mhtml,带内嵌图片,
    // Chrome/Edge/Word 可直接打开)。图表 SVG 内联,canvas/img 转部件。
    if (!articleRef.current) return;
    setDownloading(true);
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
    try {
      const title = (isStudent
        ? (c.student_name as string | undefined)
        : (c.class_name as string | undefined)) ?? "report";
      const blob = await domToMhtml(articleRef.current, `${title} ${report.period}`);
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = `${title}_${report.period}.mhtml`.replace(/\s+/g, "_");
      document.body.append(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 1000);
    } finally {
      setDownloading(false);
    }
  }

  async function downloadPdf() {
    if (!articleRef.current) return;
    // Hide the button cluster before html2canvas takes its snapshot —
    // html2canvas ignores @media print, so the print:hidden Tailwind
    // class alone isn't enough. Setting downloading=true unmounts the
    // cluster; the requestAnimationFrame yields to React so the DOM
    // commits before we capture.
    setDownloading(true);
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => resolve()),
    );
    // Build a friendly filename: <subject>_<period>_<locale-date>.pdf.
    // Falls back to "report" when the AI didn't fill in the name slot
    // (rare but possible with edited summaries).
    const subject = (
      isStudent
        ? (c.student_name as string | undefined)
        : (c.class_name as string | undefined)
    )
      ?.toString()
      .replace(/\s+/g, "_")
      .replace(/[^A-Za-z0-9_\-一-鿿]/g, "")
      || "report";
    const filename = `${subject}_${report.period}_${new Date(report.created_at).toISOString().slice(0, 10)}`;
    try {
      await downloadReportPdf({ element: articleRef.current, filename });
    } finally {
      setDownloading(false);
    }
  }

  return (
    <article
      ref={articleRef}
      className="report-print-area rounded-lg border border-slate-200 bg-white p-6"
    >
      <header className="mb-4 pb-4 border-b border-slate-100 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-medium text-slate-800">
            {isStudent
              ? (c.student_name ?? t("reports.card.fallbackStudent"))
              : (c.class_name ?? t("reports.card.fallbackClass"))}
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            {t("reports.card.meta", {
              period: report.period,
              createdAt: new Date(report.created_at).toLocaleString(locale),
            })}
          </p>
        </div>
        <div
          className="flex items-center gap-2 print:hidden"
          style={downloading ? { display: "none" } : undefined}
        >
          {editing ? (
            <>
              <button
                type="button"
                onClick={saveEdit}
                disabled={update.isPending}
                className="px-3 py-1 text-xs rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
              >
                {update.isPending
                  ? t("reports.saving")
                  : t("reports.save")}
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                className="px-3 py-1 text-xs rounded bg-slate-100 text-slate-700 hover:bg-slate-200"
              >
                {t("reports.cancel")}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={startEdit}
                className="px-3 py-1 text-xs rounded bg-slate-100 text-slate-700 hover:bg-slate-200"
              >
                {t("reports.edit")}
              </button>
              <button
                type="button"
                onClick={downloadPdf}
                disabled={downloading}
                className="px-3 py-1 text-xs rounded bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50"
              >
                {downloading
                  ? t("reports.downloading")
                  : t("reports.downloadPdf")}
              </button>
              <button
                type="button"
                onClick={() => void downloadMhtml()}
                disabled={downloading}
                className="px-3 py-1 text-xs rounded bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50"
              >
                {t("reports.downloadMhtml")}
              </button>
            </>
          )}
        </div>
      </header>

      {editing ? (
        <BulletEditor
          title={t("reports.summaryLabel")}
          help={t("reports.summaryHelp")}
          tone="neutral"
          items={draftSummaryItems}
          onChange={setDraftSummaryItems}
        />
      ) : (
        c.summary && <SummaryBullets text={c.summary as string} />
      )}

      {isStudent && c.skill_breakdown && (
        <section className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">{t("reports.card.radarTitle")}</h3>
            <SkillBreakdownView
              breakdown={c.skill_breakdown}
              overall={(c.overall_score as number | null) ?? null}
              lang={radarLang}
              labelOf={(k) => String(t(`common.skills.${k}`, { defaultValue: k }))}
              height={260}
            />
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">{t("reports.card.detailsTitle")}</h3>
            <table className="w-full text-sm">
              <thead className="text-slate-500">
                <tr>
                  <th className="text-left py-1">{t("reports.card.detailsHeaders.skill")}</th>
                  <th className="text-right py-1">{t("reports.card.detailsHeaders.score")}</th>
                  <th className="text-right py-1">{t("reports.card.detailsHeaders.trend")}</th>
                  <th className="text-right py-1">{t("reports.card.detailsHeaders.delta")}</th>
                </tr>
              </thead>
              <tbody>
                {SkillBreakdownRows({
                  breakdown: c.skill_breakdown,
                  labelOf: (k) => String(t(`common.skills.${k}`, { defaultValue: k })),
                  subjectHeader: String(t("reports.card.detailsHeaders.subject")),
                }).rows.map(
                  ({ subject, skill, info }) => (
                    <tr key={`${subject ?? ""}-${skill}`} className="border-t border-slate-100">
                      <td className="py-1 text-slate-700">
                        {subject ? `${subject} · ` : ""}
                        {t(`common.skills.${skill}`, { defaultValue: skill })}
                      </td>
                      <td className="py-1 text-right font-mono">{info.score ?? "—"}</td>
                      <td className="py-1 text-right text-xs">
                        <TrendBadge trend={info.trend ?? "stable"} />
                      </td>
                      <td className="py-1 text-right font-mono text-xs">{info.delta ?? "—"}</td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {!isStudent && c.distribution && (
        <section className="mb-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">{t("reports.card.distributionTitle")}</h3>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <DistributionChart
              distribution={
                c.distribution as {
                  excellent_85_100: number;
                  good_70_84: number;
                  average_60_69: number;
                  needs_improvement_0_59: number;
                  not_assessed: number;
                }
              }
            />
          </div>
        </section>
      )}

      {!isStudent && c.skill_averages && (
        <section className="mb-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">{t("reports.card.classRadarTitle")}</h3>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <RadarChart
              data={{
                axes: Object.entries(c.skill_averages as Record<string, number | null>).map(
                  ([skill, score]) => ({ skill, score }),
                ),
                overall: (c.class_average as number | null) ?? null,
              }}
              height={260}
              lang={radarLang}
            />
          </div>
        </section>
      )}

      {editing ? (
        <BulletEditor
          title={t("reports.card.strengthsTitle")}
          help={t("reports.strengthsHelp")}
          tone="strength"
          items={draftStrengths}
          onChange={setDraftStrengths}
        />
      ) : (
        <ColoredSectionList
          title={t("reports.card.strengthsTitle")}
          items={c.strengths as string[] | undefined}
          tone="strength"
        />
      )}
      {editing ? (
        <BulletEditor
          title={t("reports.card.growthTitle")}
          help={t("reports.growthHelp")}
          tone="growth"
          items={draftGrowth}
          onChange={setDraftGrowth}
        />
      ) : (
        <ColoredSectionList
          title={t("reports.card.growthTitle")}
          items={c.areas_for_growth as string[] | undefined}
          tone="growth"
        />
      )}
      {editing ? (
        <BulletEditor
          title={t("reports.card.recommendationsTitle")}
          help={t("reports.recsHelp")}
          tone="recommendation"
          items={draftRecs}
          onChange={setDraftRecs}
        />
      ) : (
        <ColoredSectionList
          title={t("reports.card.recommendationsTitle")}
          items={c.recommendations as string[] | undefined}
          tone="recommendation"
        />
      )}

      <ColoredSectionList
        title={t("reports.card.diagnosticsTitle")}
        items={c.diagnostic_insights as string[] | undefined}
        tone="growth"
      />

      {!isStudent && Array.isArray(c.students_needing_attention) && c.students_needing_attention.length > 0 && (
        <section className="mb-4 rounded-lg border border-red-100 bg-red-50/30 p-4">
          <h3 className="text-sm font-semibold text-red-700 mb-2">{t("reports.card.attentionTitle")}</h3>
          <ul className="space-y-2">
            {(c.students_needing_attention as Array<{ name: string; overall: number; concern: string }>).map((s, i) => (
              <li key={i} className="text-sm border-l-2 border-red-300 pl-3">
                <div className="flex items-baseline gap-2">
                  <span className="font-medium text-slate-800">{s.name}</span>
                  <span className="text-red-700 font-mono text-xs">{s.overall}</span>
                </div>
                <div className="text-slate-600 text-xs mt-0.5">{s.concern}</div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}

function TrendBadge({ trend }: { trend: string }) {
  const { t } = useTranslation();
  const cls: Record<string, string> = {
    improving: "bg-emerald-100 text-emerald-700",
    declining: "bg-rose-100 text-rose-700",
    stable: "bg-slate-100 text-slate-700",
  };
  const label = t(`reports.trend.${trend}`, { defaultValue: trend });
  const c = cls[trend] ?? "bg-slate-100 text-slate-700";
  return <span className={`px-2 py-0.5 rounded ${c}`}>{label}</span>;
}

type SectionTone = "strength" | "growth" | "recommendation" | "neutral";

const TONE_STYLES: Record<SectionTone, { wrapper: string; title: string; marker: string }> = {
  strength: {
    wrapper: "border-emerald-200 bg-emerald-50/40",
    title: "text-emerald-800",
    marker: "border-emerald-400",
  },
  growth: {
    wrapper: "border-rose-200 bg-rose-50/40",
    title: "text-rose-800",
    marker: "border-rose-400",
  },
  recommendation: {
    wrapper: "border-sky-200 bg-sky-50/40",
    title: "text-sky-800",
    marker: "border-sky-400",
  },
  // Matches the SummaryBullets read-view container so the edit version
  // of the summary section sits inside the same visual frame.
  neutral: {
    wrapper: "border-slate-200 bg-slate-50/60",
    title: "text-slate-700",
    marker: "border-slate-300",
  },
};

function ColoredSectionList({
  title,
  items,
  tone,
}: {
  title: string;
  items?: string[];
  tone: SectionTone;
}) {
  if (!items || items.length === 0) return null;
  const t = TONE_STYLES[tone];
  return (
    <section className={`mb-4 rounded-lg border ${t.wrapper} p-4`}>
      <h3 className={`text-sm font-semibold ${t.title} mb-3`}>{title}</h3>
      <ul className="space-y-2 list-disc pl-5 marker:text-slate-400">
        {items.map((it, i) => (
          <li
            key={i}
            className="text-sm text-slate-700 leading-relaxed"
          >
            {it}
          </li>
        ))}
      </ul>
    </section>
  );
}

function BulletEditor({
  title,
  help,
  tone,
  items,
  onChange,
}: {
  title: string;
  help: string;
  tone: SectionTone;
  items: string[];
  onChange: (next: string[]) => void;
}) {
  const { t } = useTranslation();
  const styles = TONE_STYLES[tone];

  function setAt(i: number, v: string) {
    const next = items.slice();
    next[i] = v;
    onChange(next);
  }
  function removeAt(i: number) {
    onChange(items.filter((_, j) => j !== i));
  }
  function add() {
    onChange([...items, ""]);
  }

  return (
    <section className={`mb-4 rounded-lg border ${styles.wrapper} p-4`}>
      <h3 className={`text-sm font-semibold ${styles.title} mb-2`}>{title}</h3>
      <p className="text-[11px] text-slate-500 mb-2">{help}</p>
      {/* Mirror ColoredSectionList: same list-disc + indent + marker
          colour, so the edit and read views read as the same layout
          with the only difference being that each bullet's body is a
          textarea instead of static text. */}
      <ul className="space-y-1.5 list-disc pl-5 marker:text-slate-400">
        {items.map((it, i) => (
          <li
            key={i}
            className="group text-sm text-slate-700 leading-relaxed"
          >
            <div className="flex items-start gap-2">
              <BulletTextarea
                value={it}
                onChange={(v) => setAt(i, v)}
              />
              <button
                type="button"
                onClick={() => removeAt(i)}
                title={t("reports.removeItem")}
                className="opacity-0 group-hover:opacity-100 text-rose-600 hover:bg-rose-50 rounded px-1.5 py-0.5 text-xs leading-none self-start mt-0.5 transition-opacity"
              >
                ×
              </button>
            </div>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={add}
        className="mt-2 text-xs text-sky-700 hover:underline"
      >
        {t("reports.addItem")}
      </button>
    </section>
  );
}

/** Borderless auto-growing textarea used as a bullet body in edit mode.
 *  Looks like inline text until the user focuses it (a thin sky outline
 *  appears + a soft white background), so the edit view reads the same
 *  visual rhythm as the read view but stays obviously interactive on
 *  hover/focus.
 *
 *  Grow logic: on every change set the height to the scrollHeight after
 *  resetting to auto. Cheap, no layout libraries, behaves the same as
 *  the lesson-note editor on /documents. */
function BulletTextarea({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  function resize(el: HTMLTextAreaElement | null) {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }

  useEffect(() => {
    resize(ref.current);
  }, [value]);

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={1}
      className="flex-1 bg-transparent text-sm text-slate-700 leading-relaxed resize-none rounded px-1.5 py-0.5 -ml-1.5 border border-transparent hover:border-slate-200 hover:bg-white focus:border-sky-300 focus:bg-white focus:outline-none focus:ring-1 focus:ring-sky-200 transition-colors"
    />
  );
}

/** Render a free-form summary paragraph as bullets. The agent returns
 *  the summary as one long string (mock + real prompts both do this);
 *  JZ wants it laid out clause-by-clause so teachers can scan it. We
 *  split on sentence-ending punctuation in both Chinese and English,
 *  drop the "（演示模式：模拟报告。）" mock-mode footer into its own pill,
 *  and surface the rest as a bulleted list. */
function SummaryBullets({ text }: { text: string }) {
  const { demoTag, body } = splitDemoSuffix(text);
  const bullets = splitIntoBullets(body);
  if (bullets.length === 0 && !demoTag) return null;
  return (
    <section className="mb-4 rounded bg-slate-50 border border-slate-200 p-4">
      {bullets.length > 0 && (
        <ul className="space-y-1.5 list-disc pl-5 marker:text-slate-400 text-sm text-slate-700">
          {bullets.map((b, i) => (
            <li key={i} className="leading-relaxed">{b}</li>
          ))}
        </ul>
      )}
      {demoTag && (
        <div className="mt-3">
          <span className="inline-block text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-amber-100 text-amber-700">
            {demoTag}
          </span>
        </div>
      )}
    </section>
  );
}

/** Pull a "（演示模式：…）" / "(demo mode: …)" footer out so it doesn't
 *  become its own bullet. Returns the trimmed body + the demo tag (or null). */
function splitDemoSuffix(text: string): { demoTag: string | null; body: string } {
  const m = text.match(/[（(]\s*(演示模式|demo mode)[^）)]*[）)]\s*$/i);
  if (!m || m.index === undefined || !m[1]) return { demoTag: null, body: text };
  return {
    demoTag: m[1].trim(),
    body: text.slice(0, m.index).trim(),
  };
}

/** Split on Chinese (。！？；) and English (. ! ? ;) sentence punctuation,
 *  preserving them on the previous bullet. Empty / whitespace-only
 *  fragments are dropped. Falls back to the whole string as a single
 *  bullet when no separator is present.
 *
 *  Western punctuation only counts when followed by whitespace or end-
 *  of-string — otherwise decimals ("reading score 70.3") get split into
 *  "reading score 70." / "3". Chinese full-width punctuation has no
 *  decimal ambiguity so it's always a terminator. */
function splitIntoBullets(text: string): string[] {
  if (!text.trim()) return [];
  const parts = text
    .split(/(?:(?<=[。！？；])|(?<=[.!?;])(?=\s|$))\s*/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return parts.length > 0 ? parts : [text.trim()];
}
